import React from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUp, ExternalLink } from "lucide-react";
import logo from "../assets/images/logo.png";
import instagramIcon from "../assets/icons/instagram.svg";
import facebookIcon from "../assets/icons/facebook.svg";
import linkedinIcon from "../assets/icons/linkedin.svg";

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigationLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Work", href: "#work" },
    { name: "Team", href: "#team" },
    { name: "Contact", href: "#contact" }
  ];

  const services = [
    "Web Development",
    "Mobile Apps",
    "UI/UX Design",
    "Digital Marketing",
    "Brand Strategy",
    "E-commerce Solutions"
  ];

  const socialLinks = [
    {
      icon: instagramIcon,
      href: "",
      name: "Instagram"
    },
    {
      icon: facebookIcon,
      href: "https://www.facebook.com/safolife.tech",
      name: "Facebook"
    },
    {
      icon: linkedinIcon,
      href: "https://www.linkedin.com/company/safolife-technologies/?viewAsMember=true",
      name: "LinkedIn"
    }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-600/10 rounded-full mix-blend-soft-light filter blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full mix-blend-soft-light filter blur-3xl"></div>
      
      {/* Main Content */}
      <div className="relative z-10 py-20 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Top Section */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12 mb-16">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="mb-6"
              >
                <img
                  src={logo}
                  alt="SafoLife Technologies"
                  className="w-48 h-auto brightness-0 invert"
                />
              </motion.div>
              
              <p className="text-gray-300 leading-relaxed mb-6 text-lg">
                Transforming businesses through innovative digital solutions and cutting-edge technology.
              </p>

              {/* Contact Info */}
              <div className="space-y-4">
                <motion.a
                  href="tel:+971529126465"
                  className="flex items-center text-gray-300 hover:text-white transition-colors duration-300 group"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-600/30 transition-colors duration-300">
                    <Phone className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="font-medium">+92 348 717 2565</span>
                </motion.a>

                <motion.a
                  href="mailto:info@safolife.com"
                  className="flex items-center text-gray-300 hover:text-white transition-colors duration-300 group"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-600/30 transition-colors duration-300">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="font-medium">info@safolife.com</span>
                </motion.a>

                <div className="flex items-start text-gray-300 group">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-600/30 transition-colors duration-300 flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-400" />
                  </div>
                  <p className="font-medium leading-relaxed">
                    B1 Markaz, Sector B-17<br />
                    Islamabad,
                     Pakistan
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-6 text-white">Quick Links</h3>
              <ul className="space-y-3">
                {navigationLinks.map((link, index) => (
                  <motion.li key={link.name} whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                      {link.name}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-6 text-white">Our Services</h3>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <motion.li key={service} whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <a
                      href="#services"
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                      {service}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Map & Social */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
           

              {/* Social Links */}
              <div>
                <h4 className="font-semibold mb-4 text-white">Follow Us</h4>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-white/10 hover:bg-blue-600 rounded-xl flex items-center justify-center transition-all duration-300 group border border-white/20"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <img
                        src={social.icon}
                        alt={social.name}
                        className="w-6 h-6 brightness-0 invert opacity-80 group-hover:opacity-100 group-hover:brightness-100 group-hover:invert-0 transition-all duration-300"
                      />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"
          />

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row justify-between items-center"
          >
            <div className="text-gray-400 text-center md:text-left mb-4 md:mb-0">
              <p className="text-lg">
                © 2024{" "}
                <span className="text-white font-semibold">SafoLife Technologies</span>
                . All rights reserved.
              </p>
              <p className="text-sm mt-1">
                Transforming businesses through digital innovation
              </p>
            </div>

            {/* Back to Top */}
            <motion.button
              onClick={scrollToTop}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 group"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowUp className="w-5 h-5" />
              <span className="font-medium">Back to Top</span>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Decorative Gradient Border */}
      <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500"></div>
    </footer>
  );
}